<template>
  <div
    class="bg-static-default-low border-static-default-low rounded-lg relative border p-24"
    @dragenter.prevent
    @dragleave.prevent
    @dragover.prevent
    @drop.prevent
  >
    <ErrorInfoBlock v-if="isError" @try-again="send('TRY_AGAIN')" />
    <Loading v-if="isLoading" />

    <ActionsList
      v-if="state.matches('actionsList')"
      :actions="state.context.actions"
      :customerRequestId
      :pendingProblems="state.context.pendingProblems"
      :productReturnState
      :requests
      :send
      :state
    />

    <ShipBackFlow
      v-if="state.matches('shipBackFlow')"
      :customerRequestId
      :mostUrgentTask
      :orderline-public-id
      :productReturnState
      @back="send('BACK')"
      @done="handleItemShippedBack"
      @exit="send('EXIT')"
    />

    <RefundFlow
      v-if="state.matches('refundFlow')"
      :customerRequestId
      :hasActiveInsurancePolicy
      :mostUrgentTask
      :orderlineQuantity
      :productReturnState
      :refundInfo="state.context.refundInfo"
      :send
      :state
      @done="handleRefundCreated"
    />

    <SendMessageFlow
      v-if="state.matches('sendMessageFlow')"
      :customerRequestId
      :mostUrgentTask
      :productReturnState
      @back="send('BACK')"
      @done="handleMessageSent"
      @exit="send('EXIT')"
    />

    <ReportProblemFlow
      v-if="state.matches('reportProblemFlow')"
      :customerRequestId
      :mostUrgentTask
      :problemTypes="state.context.reportProblemInfo.problemTypes"
      :productReturnState
      :send
      :shouldShowSendMessage="
        state.context.reportProblemInfo.hasSelectedSendMessage
      "
      :state
      @done="handleProblemReported"
    />
  </div>
</template>

<script setup lang="ts">
import { useRoute } from '#imports'
import { computed, onMounted, onUnmounted, shallowRef } from 'vue'

import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { interpret } from 'xstate'

import type {
  CareFolder,
  Orderline,
  ProductReturnState,
} from '~/api/care-folders/types/care-folder'
import { getMostUrgentTask } from '~/scopes/customer-care/components/request.helpers'

import ErrorInfoBlock from '../../../../../../components/ErrorInfoBlock/ErrorInfoBlock.vue'

import { actionBarMachine } from './ActionBar.machine'
import ActionsList from './components/ActionsList/ActionsList.vue'
import Loading from './components/Loading/Loading.vue'
import RefundFlow from './components/RefundFlow/RefundFlow.vue'
import ReportProblemFlow from './components/ReportProblemFlow/ReportProblemFlow.vue'
import SendMessageFlow from './components/SendMessageFlow/SendMessageFlow.vue'
import ShipBackFlow from './components/ShipBackFlow/ShipBackFlow.vue'

const $httpFetch = useHttpFetch()

const props = defineProps<{
  customerRequestId: CareFolder['customerRequestId']
  // TODO: improve when backend decides if we are going to use public or internal ids
  orderlineId: number | string
  orderlineQuantity: Orderline['quantity']
  orderlinePublicId: Orderline['publicId']
  hasActiveInsurancePolicy: boolean
  productReturnState: ProductReturnState
}>()

const emit = defineEmits([
  'refund-created',
  'item-shipped-back',
  'message-sent',
  'problem-reported',
])

const route = useRoute()

const state = shallowRef(actionBarMachine.initialState)

const service = interpret(
  actionBarMachine.withConfig({
    services: {
      fetchActionsAndRequests: async () => {
        return await Promise.all([
          $httpFetch(
            '/bm/customer_request/:customerRequestId/merchant/actions',
            {
              pathParams: { customerRequestId: props.customerRequestId },
            },
          ),
          $httpFetch(
            '/seller-after-sales/api/v1/orderlines/:orderlineId/requests',
            {
              pathParams: { orderlineId: props.orderlineId },
            },
          ),
        ])
      },
    },
  }),
  {
    devTools: false,
  },
).onTransition((newState) => {
  state.value = newState
})

const { send } = service

onMounted(() => {
  service.start()
})

onUnmounted(() => {
  service.stop()
})

const isLoading = computed(() => state.value.matches('initialFetch.loading'))
const isError = computed(() => state.value.matches('initialFetch.error'))

const requests = computed(() => state.value.context.requests)

const mostUrgentTask = computed(() =>
  getMostUrgentTask(requests.value, route.query.resolution?.toString() || null),
)

function handleRefundCreated() {
  send('DONE')
  emit('refund-created')
}

function handleItemShippedBack() {
  send('DONE')
  emit('item-shipped-back')
}

function handleMessageSent() {
  send('DONE')
  emit('message-sent')
}

function handleProblemReported() {
  send('DONE')
  emit('problem-reported')
}
</script>
